import { Link, useParams } from 'react-router-dom';
import { LoadTableRow, OVERVIEW, SYSTEMS, SourceSystem, landingToMarkdown, systemBySlug, systemToMarkdown } from './systemLoadOrderContent';

const BASE_PATH = '/admin/modules/data-mapping/system-load-order';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportMarkdown(filename: string, markdown: string) {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  triggerDownload(blob, filename);
}

// Landing page — purpose, scope, and a short-description link to each system.
export default function AdminSystemLoadOrder() {
  return (
    <div>
      <Breadcrumbs current={OVERVIEW.title} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <p style={{ color: 'var(--mi-muted)', marginTop: 4, maxWidth: 760 }}>
          {OVERVIEW.purpose[0]}
        </p>
        <button
          onClick={() => exportMarkdown('system-load-order.md', landingToMarkdown())}
          style={outlineBtn}
        >
          Export to MD
        </button>
      </div>

      {OVERVIEW.purpose.slice(1).map((p, i) => (
        <p key={i} style={{ color: 'var(--mi-muted)', fontSize: 14 }}>{p}</p>
      ))}

      <div style={noteBox}>
        <strong>Scope note.</strong> {OVERVIEW.scopeNote}
      </div>

      <h2 style={sectionHeading}>Systems</h2>
      <section className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>System</th>
              <th style={th}>Covers</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {SYSTEMS.map((s) => (
              <tr key={s.slug} style={{ borderTop: '1px solid var(--mi-border)' }}>
                <td style={{ ...td, fontWeight: 600, color: 'var(--mi-navy)' }}>
                  <Link to={`${BASE_PATH}/${s.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {s.name}
                  </Link>
                </td>
                <td style={{ ...td, color: 'var(--mi-muted)' }}>{s.shortDescription}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <Link to={`${BASE_PATH}/${s.slug}`} style={{ ...outlineBtn, textDecoration: 'none' }}>View →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// Per-system detail page.
export function AdminSystemLoadOrderSystem() {
  const { slug } = useParams<{ slug: string }>();
  const system = systemBySlug(slug);

  if (!system) {
    return (
      <div>
        <Breadcrumbs current="Not found" />
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No system found with that id.</div>
      </div>
    );
  }

  const idx = SYSTEMS.findIndex((s) => s.slug === system.slug);
  const prev = idx > 0 ? SYSTEMS[idx - 1] : null;
  const next = idx < SYSTEMS.length - 1 ? SYSTEMS[idx + 1] : null;

  return (
    <div>
      <Breadcrumbs current={system.name} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
        <Link to={BASE_PATH} style={{ ...outlineBtn, textDecoration: 'none' }}>← Back to Systems</Link>
        <button
          onClick={() => exportMarkdown(`${system.slug}-load-order.md`, systemToMarkdown(system))}
          style={outlineBtn}
        >
          Export to MD
        </button>
      </div>

      <SystemSection system={system} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        {prev ? (
          <Link to={`${BASE_PATH}/${prev.slug}`} style={{ ...outlineBtn, textDecoration: 'none' }}>← {prev.name}</Link>
        ) : <span />}
        {next ? (
          <Link to={`${BASE_PATH}/${next.slug}`} style={{ ...outlineBtn, textDecoration: 'none' }}>{next.name} →</Link>
        ) : <span />}
      </div>
    </div>
  );
}

function LoadTableBlock({ row }: { row: LoadTableRow }) {
  return (
    <tr style={{ borderTop: '1px solid var(--mi-border)', ...(row.confirmed ? undefined : placeholderRow) }}>
      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--mi-navy)', whiteSpace: 'nowrap' }}>
        {row.table}
        {row.loadsToClientVerse === false && (
          <div style={notLoadedTag}>Not sent to ClientVerse</div>
        )}
      </td>
      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
        {row.dependsOn.length === 0 ? <span style={{ color: 'var(--mi-muted)', fontFamily: 'inherit' }}>none</span> : row.dependsOn.join(', ')}
      </td>
      <td style={td}>
        {!row.confirmed && <div style={placeholderTag}>Unconfirmed — ask the architect</div>}
        {row.reason}
      </td>
    </tr>
  );
}

function SystemSection({ system }: { system: SourceSystem }) {
  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-purple)' }}>
      {system.overview.map((p, i) => (
        <p key={i} style={{ marginTop: i === 0 ? 0 : undefined, fontSize: 14 }}>{p}</p>
      ))}

      {system.waves.map((wave, wi) => (
        <div key={wi} style={{ marginBottom: 16 }}>
          <SectionHeading>{wave.label}</SectionHeading>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '22%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th style={th}>Table</th>
                <th style={th}>Depends on</th>
                <th style={th}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {wave.tables.map((row, ri) => <LoadTableBlock key={ri} row={row} />)}
            </tbody>
          </table>
        </div>
      ))}

      {system.crossSystemNotes && system.crossSystemNotes.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Relationship to other systems’ load order</SectionHeading>
          {system.crossSystemNotes.map((n, i) => (
            <p key={i} style={{ fontSize: 14, color: 'var(--mi-muted)' }}>{n}</p>
          ))}
        </div>
      )}

      {system.openQuestions && system.openQuestions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Open questions for the ClientVerse architect</SectionHeading>
          <div style={openQuestionsBox}>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {system.openQuestions.map((q, i) => <li key={i} style={{ marginBottom: i < system.openQuestions!.length - 1 ? 8 : 0 }}>{q}</li>)}
            </ol>
          </div>
        </div>
      )}

      {(system.notes ?? []).map((n, i) => (
        <p key={i} style={{ fontSize: 14, color: 'var(--mi-muted)' }}>{n}</p>
      ))}
    </section>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <>
      <hr style={sectionRule} />
      <h2 style={sectionTitle}>{children}</h2>
    </>
  );
}

function Breadcrumbs({ current }: { current: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <Link to={BASE_PATH} style={breadcrumb}>System Load Order</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <h1 style={{ color: 'var(--mi-navy)', margin: 0, fontSize: 20 }}>{current}</h1>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Styles — same conventions as AdminSystemToClientverse.tsx.
// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const sectionHeading: React.CSSProperties = { color: 'var(--mi-navy)', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 24, marginBottom: 12 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--mi-navy)', textTransform: 'uppercase', letterSpacing: 0.5,
  borderBottom: '1px solid var(--mi-border)', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
const outlineBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)',
  color: 'var(--mi-navy)', padding: '6px 12px', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, fontWeight: 500,
};
const noteBox: React.CSSProperties = {
  background: 'var(--mi-bg-alt, #f6f7fb)', border: '1px solid var(--mi-border)',
  borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--mi-muted)',
  margin: '12px 0',
};
const sectionRule: React.CSSProperties = {
  border: 'none', borderTop: '1px solid var(--mi-border)', margin: '24px 0 16px',
};
const sectionTitle: React.CSSProperties = {
  fontSize: 18, fontWeight: 700, color: 'var(--mi-navy)', margin: '0 0 10px',
};
const openQuestionsBox: React.CSSProperties = {
  background: '#fff8e6', border: '1px solid #f0dca3', borderRadius: 8,
  padding: '12px 16px', fontSize: 14,
};
// A row is a guess, not verified schema — a faint tint on the whole row plus
// a leading tag keeps that visible even if someone skims past the Reason
// cell, matching the placeholder-row convention on the Steps pages.
const placeholderRow: React.CSSProperties = {
  background: '#faf6ee',
};
const placeholderTag: React.CSSProperties = {
  display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
  color: '#8a5a00', background: '#fff2cc', border: '1px solid #f0dca3', borderRadius: 4,
  padding: '1px 6px', marginBottom: 4,
};
const notLoadedTag: React.CSSProperties = {
  marginTop: 4, fontSize: 10, fontWeight: 600, letterSpacing: 0.2,
  color: 'var(--mi-muted)', fontFamily: 'inherit', whiteSpace: 'normal',
};
